import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  FormControl,
  FormHelperText,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { FileCopy, Visibility, VisibilityOff } from "@material-ui/icons";
import { toast } from "react-toastify";

import useCompanySettings from "../../hooks/useSettings/companySettings";
import { BACKEND_URL } from "../../config/env";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  subtitle: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
}));

const MetaConfig = ({ settings }) => {
  const classes = useStyles();
  const { update } = useCompanySettings();

  const [facebookAppId, setFacebookAppId] = useState("");
  const [facebookAppSecret, setFacebookAppSecret] = useState("");
  const [facebookVerifyToken, setFacebookVerifyToken] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [copiedToken, setCopiedToken] = useState(false);
  const [copiedWebhookUrl, setCopiedWebhookUrl] = useState(false);

  const webhookUrl = `${BACKEND_URL}/webhook`;

  useEffect(() => {
    const incomingAppId = settings?.facebookAppId || "";
    const incomingAppSecret = settings?.facebookAppSecret || "";
    const incomingVerifyToken = settings?.facebookVerifyToken || "";

    setFacebookAppId(incomingAppId);
    setFacebookAppSecret(incomingAppSecret);

    if (incomingVerifyToken) {
      setFacebookVerifyToken(incomingVerifyToken);
      return;
    }

    const autoToken =
      "chatia_" +
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);

    setFacebookVerifyToken(autoToken);
    update({ column: "facebookVerifyToken", data: autoToken });
  }, [settings, update]);

  const handleCopy = async (value, type) => {
    try {
      await navigator.clipboard.writeText(value);
      if (type === "token") {
        setCopiedToken(true);
        setTimeout(() => setCopiedToken(false), 2000);
      } else {
        setCopiedWebhookUrl(true);
        setTimeout(() => setCopiedWebhookUrl(false), 2000);
      }
    } catch (_error) {
      toast.error("No se pudo copiar al portapapeles.");
    }
  };

  return (
    <div className={classes.root}>
      <Typography variant="h6" className={classes.sectionTitle}>
        Integración Meta
      </Typography>
      <Typography className={classes.subtitle}>
        Configure aquí los datos de su aplicación de Meta for Developers para conectar Facebook Messenger e Instagram Direct.
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth className={classes.formField}>
            <TextField
              id="facebookAppId"
              name="facebookAppId"
              label="Facebook App ID"
              variant="outlined"
              value={facebookAppId}
              placeholder="Ej: 123456789012345"
              onChange={(e) => setFacebookAppId(e.target.value)}
              onBlur={async () => {
                await update({ column: "facebookAppId", data: facebookAppId });
              }}
            />
            <FormHelperText>
              Encuéntrelo en Meta for Developers &gt; App &gt; Configuración &gt; Básico.
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth className={classes.formField}>
            <TextField
              id="facebookAppSecret"
              name="facebookAppSecret"
              label="Facebook App Secret"
              variant="outlined"
              type={showSecret ? "text" : "password"}
              value={facebookAppSecret}
              placeholder="Clave secreta de la app"
              onChange={(e) => setFacebookAppSecret(e.target.value)}
              onBlur={async () => {
                await update({ column: "facebookAppSecret", data: facebookAppSecret });
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowSecret((prev) => !prev)}
                    >
                      {showSecret ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormHelperText>
              Clave secreta de la aplicación. No la comparta.
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth className={classes.formField}>
            <TextField
              id="facebookVerifyToken"
              name="facebookVerifyToken"
              label="Token de verificación del webhook"
              variant="outlined"
              value={facebookVerifyToken}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(facebookVerifyToken, "token")}
                    >
                      <FileCopy fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormHelperText>
              {copiedToken
                ? "Token copiado."
                : "Copie este token y péguelo en la configuración del webhook en Meta."}
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth className={classes.formField}>
            <TextField
              id="webhookUrl"
              name="webhookUrl"
              label="URL de callback del webhook"
              variant="outlined"
              value={webhookUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(webhookUrl, "url")}
                    >
                      <FileCopy fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormHelperText>
              {copiedWebhookUrl
                ? "URL copiada."
                : "Pegue esta URL en el campo de callback del webhook en Meta for Developers."}
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
    </div>
  );
};

export default MetaConfig;
